import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

// Настраиваем axios для отправки cookies с каждым запросом
axios.defaults.withCredentials = true;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const isFetchingUserRef = React.useRef(false);
  const isLoggingOutRef = React.useRef(false);
  const sessionExpiredRef = React.useRef(false);

  const fetchUser = React.useCallback(async () => {
    // Защита от повторных запросов
    if (isFetchingUserRef.current) {
      return;
    }

    isFetchingUserRef.current = true;
    try {
      // Cookie автоматически отправляется с запросом
      const response = await axios.get('/api/users/me');
      setUser(response.data);
    } catch (error) {
      // 401 - это нормально для неавторизованного пользователя, не логируем как ошибку
      if (error.response?.status !== 401) {
        console.error('Error fetching user:', error);
      }
      setUser(null);
    } finally {
      setLoading(false);
      isFetchingUserRef.current = false;
    }
  }, []);

  // Инициализация при загрузке приложения
  useEffect(() => {
    const initializeAuth = async () => {
      // Всегда проверяем авторизацию, даже на публичных страницах
      // Это нужно, чтобы авторизованные пользователи автоматически редиректились в личный кабинет
      await fetchUser();
      setIsInitialized(true);
    };

    if (!isInitialized) {
      initializeAuth();
    }
  }, [fetchUser, isInitialized]);

  // Глобальный перехватчик ошибок аутентификации
  useEffect(() => {
    if (!isInitialized) return;

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          // Проверяем, что это не запрос к /api/users/me или /api/auth/login
          const isAuthRequest = error.config?.url?.includes('/api/users/me') || 
                                error.config?.url?.includes('/api/auth/login') ||
                                error.config?.url?.includes('/api/auth/logout') ||
                                error.config?.url?.includes('/api/vk-auth/') ||
                                error.config?.url?.includes('/api/yandex-auth/') ||
                                error.config?.url?.includes('/api/google-auth/') ||
                                error.config?.url?.includes('/api/courses/request');
          
          // Только для запросов к защищенным эндпоинтам проверяем сессию
          if (!isAuthRequest && !isLoggingOutRef.current && user) {
            // Проверяем, действительно ли сессия истекла, делая запрос к /api/users/me
            try {
              await axios.get('/api/users/me');
              // Если запрос успешен, значит сессия еще валидна
              // Это была ошибка другого типа, не связанная с аутентификацией
              return Promise.reject(error);
            } catch (checkError) {
              // Сессия действительно истекла
              if (!sessionExpiredRef.current) {
                sessionExpiredRef.current = true;
                toast.error('Сессия истекла. Пожалуйста, войдите заново.');
                setUser(null);
                // Сбрасываем флаг через короткий интервал, чтобы не дублировать сообщения
                setTimeout(() => {
                  sessionExpiredRef.current = false;
                }, 2000);
              }
            }
          }
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor при размонтировании
    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [isInitialized, user]);
  
  // Периодическая проверка сессии (каждый час)
  // Токен живет 30 дней, но проверяем каждый час на случай проблем с сетью
  useEffect(() => {
    if (!user || !isInitialized) return;
    
    const checkSessionInterval = setInterval(async () => {
      try {
        await axios.get('/api/users/me');
        // Сессия валидна, ничего не делаем
      } catch (error) {
        if (error.response?.status === 401) {
          // Сессия истекла
          if (!sessionExpiredRef.current && !isLoggingOutRef.current) {
            sessionExpiredRef.current = true;
            toast.error('Сессия истекла. Пожалуйста, войдите заново.');
            setUser(null);
            setTimeout(() => {
              sessionExpiredRef.current = false;
            }, 2000);
          }
        }
      }
    }, 60 * 60 * 1000); // Проверяем каждый час (токен живет 30 дней)
    
    return () => clearInterval(checkSessionInterval);
  }, [user, isInitialized]);

  const login = async (username, password, termsAccepted = false, ref = null) => {
    try {
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);
      formData.append('terms_accepted', termsAccepted ? 'true' : 'false');
      if (ref) {
        formData.append('ref', ref);
      }

      // Отправляем запрос, cookie установится автоматически
      await axios.post('/api/auth/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      // Загружаем данные пользователя (cookie уже установлен)
      await fetchUser();
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Ошибка входа' 
      };
    }
  };

  const logout = async () => {
    try {
      // Устанавливаем флаг logout, чтобы не показывать toast для 401 ошибок
      isLoggingOutRef.current = true;
      
      // Отправляем запрос на backend для удаления cookie
      await axios.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // В любом случае очищаем состояние на фронте
      setUser(null);
      
      // Переход на лендинг
      window.location.href = '/';
      
      // Сбрасываем флаг после небольшой задержки
      setTimeout(() => {
        isLoggingOutRef.current = false;
      }, 500);
    }
  };

  const updateBalance = React.useCallback((balance) => {
    const value = Number(balance);
    if (!Number.isFinite(value)) return;
    const next = Math.round(value * 100) / 100;
    setUser((prev) => {
      if (!prev) return prev;
      const current = Math.round(Number(prev.bonus_balance) * 100);
      if (current === Math.round(next * 100)) return prev;
      return { ...prev, bonus_balance: next };
    });
  }, []);

  const value = {
    user,
    login,
    logout,
    loading,
    fetchUser,  // Экспортируем fetchUser для использования в компонентах
    updateBalance,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
